import React, { useRef } from 'react';
import clsx from 'clsx';
import { AEyebrow, AEyebrowVariant } from '../../atoms/AEyebrow';
import { AHeader, AHeaderSize, AHeaderTag } from '../../atoms/AHeader';
import { ASubheader, ASubheaderSize, ASubheaderTag } from '../../atoms/ASubheader';
import { MParagraph, MParagraphAlignment } from '../MParagraph';
import { MBlockquote, MBlockquoteAlignment } from '../MBlockquote';
import { OMediaCaption } from '../../organisms/OMediaCaption';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement } from '../MButton';
import { ABodySize, ABodyColor } from '../../atoms/ABody';
import { ALinkStandaloneSize } from '../../atoms/ALinkStandalone';
import * as Styled from './MTextBlockEditorial.styles';
import type { MTextBlockEditorialProps } from './MTextBlockEditorial.types';
import { useTracking } from '../../hooks';

const COMPONENT_NAME = 'm-web-ui-kit-MTextBlockEditorial';
const TEST_ID = 'web-ui-kit-MTextBlockEditorial';

export const MTextBlockEditorial = React.forwardRef<HTMLDivElement, MTextBlockEditorialProps>((props, ref) => {
  const {
    aEyebrow,
    aHeader,
    editorialId,
    aSubheader,
    mParagraph,
    mBlockquote,
    oMediaCaption,
    mButton,
    mButton2,
    className,
    trackingProperties,
    custom_click_track_value,
    preTrackVal,
    ...rest
  } = props;

  const internalRef = useRef<HTMLDivElement>(null);
  const elementRef = (ref ?? internalRef) as React.RefObject<HTMLDivElement>;

  useTracking({
    ref: elementRef,
    trackingProperties,
    custom_click_track_value,
    preTrackVal,
    text: aHeader?.text,
  });

  return (
    <Styled.Wrapper
      ref={elementRef}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('m-text-block-editorial', className)}
      {...rest}
    >
      <Styled.ContentWrapper>
        {aEyebrow?.eyebrowText && (
          <Styled.EyebrowWrapper>
            <AEyebrow eyebrowText={aEyebrow.eyebrowText} variant={aEyebrow.variant ?? AEyebrowVariant.Primary} />
          </Styled.EyebrowWrapper>
        )}

        {aHeader?.text && (
          <Styled.HeaderWrapper>
            <AHeader
              text={aHeader.text}
              size={aHeader.size ?? AHeaderSize.TitleL}
              headerTag={aHeader.headerTag ?? AHeaderTag.h1}
              id={editorialId}
            />
          </Styled.HeaderWrapper>
        )}

        {aSubheader?.text && (
          <Styled.SubheaderWrapper>
            <ASubheader
              text={aSubheader.text}
              size={aSubheader.size ?? ASubheaderSize.XL}
              element={aSubheader.element ?? ASubheaderTag.h3}
            />
          </Styled.SubheaderWrapper>
        )}

        {mParagraph && (
          <Styled.ParagraphWrapper>
            <MParagraph
              alignment={mParagraph.alignment ?? MParagraphAlignment.Left}
              truncated={String(mParagraph.truncated).toLowerCase() === 'true'}
              truncateLines={mParagraph.truncateLines}
              expanded={mParagraph.expanded ?? false}
              aBody={{
                bodyText: mParagraph.aBody?.bodyText ?? '',
                size: mParagraph.aBody?.size ?? ABodySize.M,
                color: mParagraph.aBody?.color ?? ABodyColor.Default,
              }}
              aLinkStandalone={
                mParagraph.aLinkStandalone
                  ? {
                      text: mParagraph.aLinkStandalone.text ?? 'Read More',
                      size: mParagraph.aLinkStandalone.size ?? ALinkStandaloneSize.Small,
                      externalLink: mParagraph.aLinkStandalone.externalLink ?? false,
                    }
                  : undefined
              }
            />
          </Styled.ParagraphWrapper>
        )}

        {mBlockquote && (
          <Styled.BlockquoteWrapper>
            <MBlockquote
              alignment={mBlockquote.alignment ?? MBlockquoteAlignment.Left}
              quoteIcon={
                mBlockquote.quoteIcon ??
                (mBlockquote.aBlockquote?.text || mBlockquote.aBlockquote?.quoteText ? true : false)
              }
              blockquoteText={mBlockquote.aBlockquote?.text ?? mBlockquote.aBlockquote?.quoteText ?? ''}
              bodyText={mBlockquote.aBody?.bodyText ?? ''}
              bodyColor={mBlockquote.aBody?.color ?? ABodyColor.Default}
              bodySize={mBlockquote.aBody?.size ?? ABodySize.S}
            />
          </Styled.BlockquoteWrapper>
        )}

        {oMediaCaption && oMediaCaption.aBody && (
          <Styled.MediaCaptionWrapper>
            <OMediaCaption mMediaSelector={oMediaCaption.mMediaSelector} aBody={oMediaCaption.aBody} />
          </Styled.MediaCaptionWrapper>
        )}

        {(mButton?.buttonText || mButton2?.buttonText) && (
          <Styled.ButtonWrapper>
            {mButton?.buttonText && (
              <MButton
                buttonText={mButton.buttonText}
                buttonType={mButton.buttonType ?? MButtonType.Primary}
                size={mButton.size ?? MButtonSize.M}
                iconPlacement={mButton.iconPlacement ?? MButtonIconPlacement.None}
                iconClass={mButton.iconClass}
                aIcon={mButton.aIcon}
                ctaLink={mButton.ctaLink}
                openInANewTab={mButton.openInANewTab ?? false}
                disabled={mButton.disabled ?? false}
                trackingProperties={mButton.trackingProperties ?? trackingProperties}
              />
            )}
            {mButton2?.buttonText && (
              <MButton
                buttonText={mButton2.buttonText}
                buttonType={mButton2.buttonType ?? MButtonType.Secondary}
                size={mButton2.size ?? MButtonSize.M}
                iconPlacement={mButton2.iconPlacement ?? MButtonIconPlacement.None}
                iconClass={mButton2.iconClass}
                aIcon={mButton2.aIcon}
                ctaLink={mButton2.ctaLink}
                openInANewTab={mButton2.openInANewTab ?? false}
                disabled={mButton2.disabled ?? false}
                trackingProperties={mButton2.trackingProperties ?? trackingProperties}
              />
            )}
          </Styled.ButtonWrapper>
        )}
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
});

MTextBlockEditorial.displayName = 'MTextBlockEditorial';

export { TEST_ID as M_TEXT_BLOCK_EDITORIAL_TEST_ID };
